import React, { useEffect, useState } from "react";
import { View, Pressable, ScrollView, StyleSheet } from "react-native";
import DsModal from "@components/ds/DsModal";
import NativeScannerSheet from "@components/ds/NativeScannerSheet";
import { notifySuccess } from "@components/ds/notify";
import { showApiError } from "@components/ds/showApiError";
import { t } from "@constants/localization";
import { resolveRack } from "@services/api/receiving";
import { rerackDsBox } from "@services/api/dsRouting";
import ScanInput from "@screens/Receiving/components/ScanInput";
import { getRackRefusal } from "@screens/Receiving/helpers";
import { C, RText, Tag, Check, AddressLine, tpName } from "../theme";

const PATH = "DsRouting/returns";

const SCAN = { SCANNED: "SCANNED", MANUAL: "MANUAL" };

/**
 * Re-rack one customer's returned boxes: scan (or type) a rack, then the
 * boxes. The rack is checked like Receiving's (one customer per date, full
 * racks refused); each box goes through POST /returns/rerack.
 * @param customer { cx_id, cx_name, address, order_edd, boxes: [{shipment_id, last_rack_no}] } or null
 */
const RerackSheet = ({ customer, onClose, onDone }) => {
  const [rack, setRack] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [doneIds, setDoneIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [scanner, setScanner] = useState(null); // "RACK" | "BOX" | null

  useEffect(() => {
    setRack(null);
    setRefusal(null);
    setDoneIds(new Set());
    setScanner(null);
  }, [customer]);

  const boxes = customer?.boxes || [];
  const allDone =
    boxes.length > 0 && boxes.every((b) => doneIds.has(b.shipment_id));

  const pickRack = async (code) => {
    if (!customer || !code || busy) return;
    try {
      setBusy(true);
      const { data } = await resolveRack({
        code,
        cx_id: customer.cx_id,
        date: customer.order_edd,
      });
      setRack(data?.data?.rack || null);
      setRefusal(null);
    } catch (error) {
      const refused = getRackRefusal(error);
      if (refused) setRefusal(refused);
      else showApiError(error, () => "DsRouting/errorGeneric");
    } finally {
      setBusy(false);
    }
  };

  const scanBox = async (code, scanKind) => {
    if (!rack || !code || busy) return;
    try {
      setBusy(true);
      const { data } = await rerackDsBox({
        shipment_id: code,
        rack_id: rack.rack_id,
        scan_kind: scanKind,
      });
      const id = data?.data?.shipment_id || code;
      setDoneIds((prev) => new Set(prev).add(id));
      notifySuccess(t(`${PATH}/boxDone`, { rack: rack.rack_no || "" }));
    } catch (error) {
      showApiError(error, () => "DsRouting/errorGeneric");
    } finally {
      setBusy(false);
    }
  };

  const onDecode = (value) => {
    const which = scanner;
    setScanner(null);
    if (which === "RACK") pickRack(value);
    else if (which === "BOX") scanBox(value, SCAN.SCANNED);
  };

  return (
    <DsModal
      visible={Boolean(customer)}
      onClose={onClose}
      scrim={C.scrim}
      contentStyle={styles.sheet}
    >
      <View style={styles.handle} />
      <RText size={17} weight={700}>
        {customer ? tpName(customer) : ""}
      </RText>
      <AddressLine address={customer?.address} style={styles.address} />

      {!rack ? (
        <>
          <RText size={12} weight={700} color={C.muted} style={styles.step}>
            {t(`${PATH}/stepRack`)}
          </RText>
          <ScanInput
            placeholder={t(`${PATH}/rackPlaceholder`)}
            onManualSubmit={pickRack}
            onScanPress={() => setScanner("RACK")}
            disabled={busy}
            testIDPrefix="ds-rerack-rack"
          />
          {refusal ? (
            <View style={styles.refusal}>
              <RText size={12.5} weight={600} color={C.red}>
                {t(`${PATH}/rackHeld`, { rack: refusal.rackNo })}
              </RText>
              {refusal.suggestedRacks.length ? (
                <View style={styles.chips}>
                  {refusal.suggestedRacks.map((r) => (
                    <Pressable
                      key={r.rack_no}
                      onPress={() => pickRack(r.rack_code || r.rack_no)}
                      style={styles.chip}
                      testID={`ds-rerack-suggest-${r.rack_no}`}
                    >
                      <RText size={12} weight={600}>
                        {r.rack_no}
                      </RText>
                    </Pressable>
                  ))}
                </View>
              ) : null}
            </View>
          ) : null}
        </>
      ) : (
        <>
          <View style={styles.rackRow}>
            <RText size={13} weight={700}>
              {t(`${PATH}/onRack`, { rack: rack.rack_no || "" })}
            </RText>
            {!allDone ? (
              <Pressable onPress={() => setRack(null)} hitSlop={8}>
                <RText size={12} weight={700} color={C.blueLine}>
                  {t(`${PATH}/changeRack`)}
                </RText>
              </Pressable>
            ) : null}
          </View>
          {!allDone ? (
            <ScanInput
              placeholder={t(`${PATH}/boxPlaceholder`)}
              onManualSubmit={(code) => scanBox(code, SCAN.MANUAL)}
              onScanPress={() => setScanner("BOX")}
              disabled={busy}
              testIDPrefix="ds-rerack-box"
            />
          ) : null}
        </>
      )}

      <ScrollView style={styles.list} contentContainerStyle={styles.rows}>
        {boxes.map((box) => {
          const done = doneIds.has(box.shipment_id);
          return (
            <View
              key={box.shipment_id}
              style={[styles.box, done && styles.boxDone]}
              testID={`ds-rerack-box-${box.shipment_id}`}
            >
              <View style={styles.grow}>
                <RText size={12.5} weight={600}>
                  {box.shipment_id}
                </RText>
                {box.last_rack_no ? (
                  <RText size={11} weight={500} color={C.muted}>
                    {t(`${PATH}/wasOn`, { rack: box.last_rack_no })}
                  </RText>
                ) : null}
              </View>
              {done ? (
                <View style={styles.tick}>
                  <Check size={12} />
                </View>
              ) : (
                <Tag
                  label={t(`${PATH}/toScan`)}
                  bg={C.amberBg}
                  color={C.amber}
                />
              )}
            </View>
          );
        })}
      </ScrollView>

      {allDone ? (
        <Pressable
          testID="ds-rerack-done"
          onPress={onDone}
          style={styles.doneBtn}
        >
          <RText size={15} weight={600} color="#FFFFFF">
            {t(`${PATH}/allDone`, { count: String(boxes.length) })}
          </RText>
        </Pressable>
      ) : null}

      <NativeScannerSheet
        flow={scanner === "RACK" ? "receiving_rack" : "receiving_shipment"}
        visible={Boolean(scanner)}
        onClose={() => setScanner(null)}
        onDecode={onDecode}
      />
    </DsModal>
  );
};

export default RerackSheet;

const styles = StyleSheet.create({
  sheet: {
    backgroundColor: C.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 20,
    maxHeight: "88%",
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: C.border,
    alignSelf: "center",
    marginBottom: 14,
  },
  address: { paddingTop: 3 },
  step: { letterSpacing: 0.5, paddingTop: 14 },
  refusal: { gap: 8, paddingBottom: 6 },
  chips: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  chip: {
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  rackRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 14,
  },
  list: { maxHeight: 300, marginTop: 6 },
  rows: { gap: 8, paddingBottom: 6 },
  box: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  boxDone: { borderColor: C.green, backgroundColor: C.greenBg },
  grow: { flex: 1 },
  tick: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: C.green,
    alignItems: "center",
    justifyContent: "center",
  },
  doneBtn: {
    backgroundColor: C.green,
    borderRadius: 9,
    paddingVertical: 13,
    alignItems: "center",
    marginTop: 10,
  },
});
